@file:Suppress("UNCHECKED_CAST_TO_EXTERNAL_INTERFACE")

package org.example.project.enginecardview.layouts

import androidx.compose.runtime.Composable
import org.example.project.dashboard.DashboardTheme
import org.example.project.dashboard.POPUP_ENGINE_MODAL_Z_INDEX
import org.example.project.dashboard.PopupEngineCard
import org.example.project.screens_cus.infraCodeAusMeta
import org.example.project.screens_cus.infraCodeFieldOrder
import org.example.project.screens_cus.infraFieldLabel
import org.example.project.settings.LumaticaButton
import org.example.project.settings.LumaticaButtonVariant
import org.example.project.settings.LumaticaCheckbox
import org.example.project.settings.UISettings
import org.example.project.utils.All
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

internal const val REGAL_PLATZ_TYP = "Platz"

internal object RegalStyles : StyleSheet() {
    val fachCard by style {
        width(360.px)
        height(405.px)
        padding(12.px)
        borderRadius(20.px)
        property("box-shadow", "0 14px 28px ${DashboardTheme.SURFACE_SHADOW}")
        backgroundColor(DashboardTheme.SURFACE_CARD)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(12.px)
        boxSizing("border-box")
        overflow("hidden")
    }
    val cardFree by style {
        property("border", "1px solid ${DashboardTheme.BORDER_LIGHT}")
    }
    val cardOccupied by style {
        property("border", "1px solid ${UISettings.ACCENT}")
        property("box-shadow", "0 14px 28px ${DashboardTheme.SURFACE_SHADOW}, 0 0 20px rgba(255,0,0,0.16)")
    }
    val header by style {
        width(100.percent)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        justifyContent(JustifyContent.Center)
        alignItems(AlignItems.Center)
        minHeight(72.px)
        paddingTop(2.px)
        gap(8.px)
    }
    val platzLabel by style {
        fontWeight("800")
        fontSize(30.px)
        color(DashboardTheme.TEXT_LIGHT)
        lineHeight("1.15")
        textAlign("center")
        property("display", "-webkit-box")
        property("-webkit-line-clamp", "2")
        property("-webkit-box-orient", "vertical")
        property("overflow", "hidden")
        property("text-overflow", "ellipsis")
        property("word-break", "break-word")
    }
    val previewBox by style {
        width(100.percent)
        height(204.px)
        position(Position.Relative)
        flexShrink(0)
        marginTop(4.px)
        borderRadius(16.px)
        property("overflow", "hidden")
        backgroundColor(Color.transparent)
    }
    val previewContent by style {
        position(Position.Absolute)
        left(0.px)
        top(0.px)
        right(0.px)
        bottom(0.px)
        padding(16.px)
        boxSizing("border-box")
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        justifyContent(JustifyContent.Center)
        gap(12.px)
        overflowY("auto")
    }
    val entryItem by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(8.px)
    }
    val entryHeadline by style {
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.SpaceBetween)
        gap(8.px)
        fontSize(19.px)
        fontWeight("800")
        lineHeight("1.2")
        color(DashboardTheme.TEXT_LIGHT)
    }
    val entryMeta by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(7.px)
        fontSize(16.px)
        color(DashboardTheme.TEXT_MUTED)
        lineHeight("1.25")
    }
    val metaRow by style {
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.SpaceBetween)
        gap(10.px)
    }
    val metaValue by style {
        color(DashboardTheme.TEXT_LIGHT)
        fontWeight("700")
        fontSize(16.px)
        textAlign("right")
    }
    val bottomBlock by style {
        width(100.percent)
        flexGrow(1)
        minHeight(0.px)
        padding(4.px, 12.px, 10.px, 12.px)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        justifyContent(JustifyContent.SpaceBetween)
        gap(6.px)
        boxSizing("border-box")
    }
    val metaLine by style {
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.Center)
        alignItems(AlignItems.Center)
        gap(12.px)
        flexWrap(FlexWrap.Wrap)
        paddingBottom(10.px)
        overflow("hidden")
    }
    val metaText by style {
        color(DashboardTheme.TEXT_MUTED)
        fontSize(17.px)
        fontWeight("700")
        property("white-space", "nowrap")
    }
    val centerText by style {
        color(DashboardTheme.TEXT_MUTED)
    }
    val settingsPanelContent by style {
        flexGrow(1)
        minHeight(0.px)
        padding(18.px)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(16.px)
        boxSizing("border-box")
    }
    val settingsPanelTitle by style {
        color(DashboardTheme.TEXT_LIGHT)
        fontSize(20.px)
        fontWeight("800")
        lineHeight("1.2")
    }
    val settingsOptionGrid by style {
        display(DisplayStyle.Grid)
        gridTemplateColumns("repeat(3, minmax(0, 1fr))")
        gap(12.px)
    }
    val settingsOptionCard by style {
        minHeight(112.px)
        padding(16.px, 12.px)
        borderRadius(8.px)
        backgroundColor(DashboardTheme.SURFACE_CARD)
        property("border", "1px solid ${DashboardTheme.BORDER_LIGHT}")
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        justifyContent(JustifyContent.Center)
        alignItems(AlignItems.Center)
        gap(12.px)
        boxSizing("border-box")
        cursor("pointer")
    }
    val settingsOptionLabel by style {
        color(DashboardTheme.TEXT_LIGHT)
        fontSize(14.px)
        fontWeight("700")
        textAlign("center")
    }
    val settingsFooter by style {
        property("margin-top", "auto")
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.FlexEnd)
        alignItems(AlignItems.Center)
        gap(10.px)
    }
}

@Composable
internal fun RegalSettingsPopup(
    fields: Set<String>,
    availableFields: Set<String>,
    onToggle: (String, Boolean) -> Unit,
    onClose: () -> Unit,
    onSave: () -> Unit
) {
    PopupEngineCard(
        windowKey = "regal-settings-popup",
        zIndex = POPUP_ENGINE_MODAL_Z_INDEX,
        widthPx = 560,
        heightPx = 470,
        minWidthPx = 460,
        minHeightPx = 430,
        onClose = onClose
    ) {
        Div({ classes(RegalStyles.settingsPanelContent) }) {
            Div({ classes(RegalStyles.settingsPanelTitle) }) { Text("Regalansicht") }
            Div({ classes(RegalStyles.settingsOptionGrid) }) {
                infraCodeFieldOrder()
                    .filter { it in availableFields }
                    .forEach { field ->
                        RegalSettingsOption(infraFieldLabel(field), field, fields, onToggle)
                    }
            }
            Div({ classes(RegalStyles.settingsFooter) }) {
                LumaticaButton(
                    label = "Abbrechen",
                    onClick = onClose,
                    variant = LumaticaButtonVariant.Standard,
                    heightPx = 34,
                    paddingH = 12
                )
                LumaticaButton(
                    label = "Speichern",
                    onClick = onSave,
                    variant = LumaticaButtonVariant.Action,
                    heightPx = 34,
                    paddingH = 12
                )
            }
        }
    }
}

@Composable
private fun RegalSettingsOption(
    label: String,
    field: String,
    fields: Set<String>,
    onToggle: (String, Boolean) -> Unit
) {
    val checked = field in fields
    Div({
        classes(RegalStyles.settingsOptionCard)
        onClick { onToggle(field, !checked) }
    }) {
        LumaticaCheckbox(
            checked = checked,
            onChange = { next -> onToggle(field, next) },
            fontSizePx = 26
        )
        Span({ classes(RegalStyles.settingsOptionLabel) }) { Text(label) }
    }
}

@Composable
internal fun RegalEbeneRow(
    plaetze: List<All>,
    waStandorte: List<All>,
    infra: All?,
    platzTyp: String,
    regalCodeFields: Set<String>
) {
    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Row)
            gap(18.px)
        }
    }) {
        plaetze.sortedBy { it.fach.toIntOrNull() ?: 0 }.forEach { platz ->
            val wa = waStandorte.find { it.platzcode == platz.platzcode && it.zustand < 60 }
            RegalFachCard(platz, wa, infra, platzTyp, regalCodeFields)
        }
    }
}

@Composable
private fun RegalFachCard(item: All, waAuftrag: All?, infra: All?, platzTyp: String, regalCodeFields: Set<String>) {
    val occupied = waAuftrag != null
    val itemInfraValues = mapOf(
        "standort" to item.standort,
        "halle" to item.halle,
        "regal" to item.regal,
        "fach" to item.fach,
        "ebene" to item.ebene,
        "tor" to item.tor?.toString().orEmpty(),
        "bereich" to item.bereich
    )
    val displayCode = infraCodeAusMeta(
        infra = infra,
        typ = platzTyp,
        values = itemInfraValues
    )

    val paletten = waAuftrag?.palettenWerte ?: emptyMap()
    val gesamt = paletten.values.sumOf { it.toIntOrNull() ?: 0 }

    Div({
        classes(RegalStyles.fachCard)
        classes(if (occupied) RegalStyles.cardOccupied else RegalStyles.cardFree)
    }) {
        Div({ classes(RegalStyles.header) }) {
            Span({ classes(RegalStyles.platzLabel) }) {
                Text(displayCode.ifBlank { item.platzcode.ifBlank { item.id } })
            }
        }

        Div({ classes(RegalStyles.previewBox) }) {
            if (occupied) {
                Div({ classes(RegalStyles.previewContent) }) {
                    Div({ classes(RegalStyles.entryItem) }) {
                        Div({ classes(RegalStyles.entryHeadline) }) {
                            Span { Text("LS: ${waAuftrag.lieferscheinnummer.ifBlank { "-" }}") }
                            Span { Text("WA") }
                        }
                        Div({ classes(RegalStyles.entryMeta) }) {
                            RegalPlatzMetaRow("Empfänger", waAuftrag.empfaenger.ifBlank { "-" })
                            RegalPlatzMetaRow("Ort", waAuftrag.ort.ifBlank { "-" })
                            RegalPlatzMetaRow("Tor", waAuftrag.tor?.toString().orEmpty().ifBlank { "-" })
                            paletten.forEach { (typ, wert) ->
                                val n = wert.toIntOrNull() ?: 0
                                if (n > 0) RegalPlatzMetaRow(typ, n.toString())
                            }
                            RegalPlatzMetaRow("Gesamt", "$gesamt Pal.")
                        }
                    }
                }
            }
        }

        Div({ classes(RegalStyles.bottomBlock) }) {
            Div({ classes(RegalStyles.metaLine) }) {
                Span({ classes(RegalStyles.metaText) }) { Text("Halle ${item.halle.ifBlank { "-" }}") }
                Span({ classes(RegalStyles.metaText) }) { Text("|") }
                Span({ classes(RegalStyles.metaText) }) { Text("Regal ${item.regal.ifBlank { "-" }}") }
                Span({ classes(RegalStyles.metaText) }) { Text("|") }
                Span({ classes(RegalStyles.metaText) }) { Text("Fach ${item.fach.ifBlank { "-" }}") }
                Span({ classes(RegalStyles.metaText) }) { Text("|") }
                Span({ classes(RegalStyles.metaText) }) { Text("Ebene ${item.ebene.ifBlank { "-" }}") }
            }
        }
    }
}

@Composable
internal fun RegalCenterText(text: String) {
    Div({
        classes(RegalStyles.centerText)
        style {
            flex(1)
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
        }
    }) { Text(text) }
}

@Composable
private fun RegalPlatzMetaRow(label: String, value: String) {
    Div({ classes(RegalStyles.metaRow) }) {
        Span { Text(label) }
        Span({ classes(RegalStyles.metaValue) }) { Text(value) }
    }
}
